/*
  Zellbezüge: aus A1 werden Zahlen und zurück.

  ---------------------------------------------------------------
  Warum die A1-Schreibweise überhaupt vorkommt
  ---------------------------------------------------------------

  Im Datenmodell haben Zeilen und Spalten Zufallskennungen — nur so überleben
  Kommentare und Umsortieren. Formeln benutzen trotzdem A1, B2, C7: Das ist
  die Schreibweise, die jeder aus Excel kennt, und sie soll auch in der
  Eingabezeile stehen.

  Die Position ist deshalb kein gespeicherter Wert, sondern eine ABLEITUNG
  aus der Sortierung: Die dritte Spalte heißt C, weil sie an dritter Stelle
  steht — nicht, weil irgendwo „C" geschrieben stünde.

  ---------------------------------------------------------------
  Das Dollarzeichen
  ---------------------------------------------------------------

  `$` friert einen Teil des Bezugs beim KOPIEREN ein, nicht beim Einfügen
  von Zeilen. `B$6` heißt: Kopiere mich nach rechts, dann wandert das B mit,
  die 6 bleibt. Wer eine Zeile über Zeile 6 einfügt, verschiebt trotzdem
  auch `B$6` auf `B$7` — genau wie Excel. Hier wird das Zeichen nur gelesen
  und wieder geschrieben; die Bedeutung steckt im Verschieber.
*/

const A = "A".charCodeAt(0);

/*
  Aus einer Nummer wird ein Name: 0 → A, 25 → Z, 26 → AA.

  Das ist kein normales Zahlensystem zur Basis 26, sondern eines ohne Null —
  auf Z folgt AA und nicht BA. Deshalb das `- 1` in der Schleife; ohne das
  ist die 27. Spalte falsch benannt, und zwar erst ab dort, wo es beim
  flüchtigen Prüfen niemand mehr nachzählt.
*/
export function spaltenname(nummer) {
  if (!Number.isInteger(nummer) || nummer < 0) return "";
  let rest = nummer;
  let name = "";
  while (rest >= 0) {
    name = String.fromCharCode(A + (rest % 26)) + name;
    rest = Math.floor(rest / 26) - 1;
  }
  return name;
}

/* Und zurück: A → 0, Z → 25, AA → 26. Unbekanntes ergibt -1. */
export function spaltennummer(name) {
  if (typeof name !== "string" || !name) return -1;
  const gross = name.toUpperCase();
  let nummer = 0;
  for (const zeichen of gross) {
    const stelle = zeichen.charCodeAt(0) - A;
    if (stelle < 0 || stelle > 25) return -1;
    nummer = nummer * 26 + (stelle + 1);
  }
  return nummer - 1;
}

/*
  Einen Bezug lesen: "B$6" → { spalte: 1, zeile: 5, spalteFest: false, zeileFest: true }

  Gezählt wird ab null, angezeigt ab eins — die Zeile 1 auf dem Bildschirm
  ist intern die 0. Diese eine Verschiebung ist die häufigste Fehlerquelle in
  Tabellencode, deshalb steht sie ausschließlich hier und nirgends sonst.

  `null` heißt: kein Bezug. Nicht etwa ein Bezug auf Zeile 0.
*/
export function liesBezug(text) {
  if (typeof text !== "string") return null;
  const treffer = /^(\$?)([A-Za-z]{1,3})(\$?)([0-9]{1,7})$/.exec(text.trim());
  if (!treffer) return null;

  const spalte = spaltennummer(treffer[2]);
  const zeile = Number(treffer[4]) - 1;
  if (spalte < 0 || zeile < 0) return null;

  return {
    spalte,
    zeile,
    spalteFest: treffer[1] === "$",
    zeileFest: treffer[3] === "$",
  };
}

/* Und zurück in die Schreibweise, die der Nutzer sieht. */
export function schreibBezug(bezug) {
  if (!bezug || bezug.spalte < 0 || bezug.zeile < 0) return "#BEZUG!";
  const s = bezug.spalteFest ? "$" : "";
  const z = bezug.zeileFest ? "$" : "";
  return `${s}${spaltenname(bezug.spalte)}${z}${bezug.zeile + 1}`;
}

/*
  Der Schlüssel, unter dem eine Zelle im Yjs-Dokument liegt.

  Kennungen statt Positionen — eine Zelle muss dieselbe bleiben, wenn davor
  eine Spalte eingefügt wird. Der senkrechte Strich taugt als Trenner, weil
  `neueId` ihn nie erzeugt.

  Achtung auf die Länge: `notizen.absatz` fasst 64 Zeichen, und dort landet
  genau dieser Schlüssel. Zwei Kennungen à ~18 Zeichen plus Trenner passen
  mit Abstand — aber wer die Kennungen verlängert, sprengt das Feld.
*/
export function zellschluessel(spalteId, zeileId) {
  return `${spalteId}|${zeileId}`;
}

export function teileSchluessel(schluessel) {
  if (typeof schluessel !== "string") return null;
  const strich = schluessel.indexOf("|");
  if (strich < 1 || strich === schluessel.length - 1) return null;
  return {
    spalteId: schluessel.slice(0, strich),
    zeileId: schluessel.slice(strich + 1),
  };
}
